'use client';

import { Tabs } from '@app/app/_components/Tabs/Tabs';
import { MobileIdLoginSection } from '@app/app/_widgets/Login/sections/MobileIdLoginSection';
import { SmartIdLoginSection } from '@app/app/_widgets/Login/sections/SmartIdLoginSection';
import { useTranslations } from 'next-intl';
import { EBanksLoginSection } from './sections/EBanksLoginSection';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { TenantMetadata } from '@app/infrastructure/tenant/model/TenantMetadata';

interface Props {
    onSuccess?: () => void;
    swedbankLogoSrc?: string;
    noBankAuth?: boolean;
    sebLogoSrc?: string;
    tenant?: TenantMetadata;
}

export function LoginWidget({ onSuccess, swedbankLogoSrc, noBankAuth = false, sebLogoSrc, tenant }: Props) {
    const t = useTranslations();
    const appSettings = useAppParameters();

    const hasAtLeastOneBankAuth =
        (appSettings?.settings.general.swedbankAuth || appSettings.settings.general.sebAuth) && !noBankAuth;

    const domain = tenant?.tenant;

    return (
        <>
            <div className="flex flex-col gap-4">
                <h1 className="text-center text-3xl font-medium">{t('auth.loginTitle')}</h1>
                <p className="text-center text-neutral-500">{t('auth.loginDescription')}</p>
            </div>

            <Tabs
                className="gap-6"
                content={[
                    {
                        id: 'smart-id',
                        label: t('auth.loginMethods.smartId'),
                        content: <SmartIdLoginSection onSuccess={onSuccess} domain={domain} />,
                    },
                    {
                        id: 'mobile-id',
                        label: t('auth.loginMethods.mobileId'),
                        content: <MobileIdLoginSection onSuccess={onSuccess} />,
                    },
                    ...(hasAtLeastOneBankAuth
                        ? [
                              {
                                  id: 'e-banks',
                                  label: t('auth.loginMethods.eBanks'),
                                  content: (
                                      <EBanksLoginSection
                                          swedbankLogoSrc={swedbankLogoSrc}
                                          sebLogoSrc={sebLogoSrc}
                                          swedbankAuth={appSettings?.settings.general.swedbankAuth}
                                          sebAuth={appSettings?.settings.general.sebAuth}
                                      />
                                  ),
                              },
                          ]
                        : []),
                ]}
                aria-label={t('auth.loginMethod')}
            />
        </>
    );
}
